(() => {
  'use strict';

  const key = '__nahtmaterialTaxIdStore';
  const eventName = 'nahtmaterial:tax-id-state';
  const countries = new Set('DE AT BE BG CY CZ DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK'.split(' '));
  const prefixes = new Set('AT BE BG CY CZ DE DK EE EL ES FI FR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK'.split(' '));

  const normalize = (value) => String(value || '').toUpperCase().replace(/[\s.-]/g, '');
  const classify = (data) => {
    const country = String(data.country || '').toUpperCase();
    const number = normalize(data.number);
    if (!countries.has(country)) return { country: '', number: '', type: '', valid: false, error: 'country' };
    if (country === 'DE') return { country, number: '', type: '', valid: true, error: '' };
    const prefix = number.slice(0, 2);
    const body = number.slice(2);
    const valid = prefixes.has(prefix) && /^[A-Z0-9]{6,14}$/.test(body) && /[1-9]/.test(body);
    return { country, number: valid ? number : '', type: valid ? 'EU_VAT' : '', valid, error: valid ? '' : 'number' };
  };

  if (!window[key]) {
    let state = { status: 'loading', country: '', number: '', type: '', ready: false, error: '' };
    let initialization;
    let queue = Promise.resolve();
    let sequence = 0;
    const publish = (next) => {
      state = next;
      document.dispatchEvent(new CustomEvent(eventName, { detail: next }));
    };
    const url = (name) => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + name;
    const edit = (data) => {
      const result = classify(data);
      publish({ ...result, number: normalize(data.number), status: 'editing', ready: false });
    };
    const write = (data) => {
      const requestId = ++sequence;
      const result = classify(data);
      publish({ ...result, number: normalize(data.number), status: 'saving', ready: false });
      const attributes = {
        tax_id_delivery_country: result.country,
        tax_id: result.number,
        tax_id_type: result.type,
      };
      const operation = queue.catch(() => undefined).then(async () => {
        const response = await fetch(url('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error('Tax ID save failed');
        const cart = await response.json();
        if (!Object.entries(attributes).every(([name, value]) => String(cart.attributes?.[name] || '') === value)) {
          throw new Error('Tax ID was not persisted');
        }
        return cart;
      });
      queue = operation;
      return operation.then(
        (cart) => {
          if (requestId === sequence) publish({ ...result, number: normalize(data.number), status: 'saved', ready: result.valid });
          return cart;
        },
        (error) => {
          if (requestId === sequence) publish({ ...result, number: normalize(data.number), status: 'error', ready: false, error: 'save' });
          throw error;
        },
      );
    };
    const initialize = (fallback = {}) => {
      if (!initialization) {
        const start = sequence;
        initialization = (async () => {
          try {
            const response = await fetch(url('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error('Cart load failed');
            const cart = await response.json();
            if (sequence !== start || state.status === 'editing') return;
            const attributes = cart.attributes || {};
            const result = classify({
              country: attributes.tax_id_delivery_country || fallback.country,
              number: attributes.tax_id || fallback.number,
            });
            const stored = String(attributes.tax_id_delivery_country || '') === result.country &&
              String(attributes.tax_id || '') === result.number;
            publish({ ...result, status: 'loaded', ready: stored && result.valid });
            if (!stored && result.valid) await write(result);
          } catch {
            if (sequence === start) publish({ ...state, status: 'error', ready: false, error: 'save' });
          }
        })();
      }
      return initialization;
    };
    window[key] = { get state() { return state; }, normalize, classify, initialize, edit, write };
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      initialization = undefined;
      publish({ ...state, status: 'loading', ready: false });
      void initialize();
    });
  }

  const store = window[key];
  if (!customElements.get('nahtmaterial-tax-id-fields')) {
    customElements.define('nahtmaterial-tax-id-fields', class extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.country = this.querySelector('[data-tax-country]');
        this.number = this.querySelector('[data-tax-number]');
        this.panel = this.querySelector('[data-tax-number-panel]');
        this.type = this.querySelector('[data-tax-type]');
        this.error = this.querySelector('[data-tax-error]');
        this.status = this.querySelector('[data-tax-status]');
        this.retry = this.querySelector('[data-tax-retry]');
        if (!this.country || !this.number) return;
        const options = { signal: this.abortController.signal };
        this.country.addEventListener('change', () => {
          if (this.country.value === 'DE') this.number.value = '';
          this.commit();
        }, options);
        this.number.addEventListener('input', () => {
          clearTimeout(this.timer);
          this.showErrors = false;
          store.edit(this.values());
          this.timer = setTimeout(() => this.commit(), 350);
        }, options);
        this.number.addEventListener('blur', () => this.commit(), options);
        this.retry.addEventListener('click', () => this.commit(), options);
        document.addEventListener(eventName, (event) => this.synchronize(event.detail), options);
        document.addEventListener('submit', (event) => {
          if (event.submitter?.getAttribute('name') !== 'checkout') return;
          if (!this.contains(event.submitter) && !this.closest('nahtmaterial-agb-checkout-gate')?.contains(event.submitter)) return;
          if (store.state.ready) return;
          event.preventDefault();
          this.showErrors = true;
          this.synchronize(store.state);
          this.reportValidity();
        }, { ...options, capture: true });
        this.synchronize(store.state);
        void store.initialize({ country: this.dataset.initialCountry, number: this.dataset.initialNumber });
      }
      disconnectedCallback() {
        clearTimeout(this.timer);
        this.abortController?.abort();
      }
      values() { return { country: this.country.value, number: this.number.value }; }
      commit() {
        clearTimeout(this.timer);
        this.showErrors = true;
        void store.write(this.values()).catch(() => undefined);
      }
      reportValidity() {
        const field = this.country.value && this.country.value !== 'DE' ? this.number : this.country;
        field.focus();
        field.reportValidity();
      }
      synchronize(next) {
        if (!this.country || !this.number) return;
        const activeNumber = document.activeElement === this.number;
        this.country.value = next.country;
        if (!activeNumber) this.number.value = next.number;
        const foreign = countries.has(next.country) && next.country !== 'DE';
        this.panel.hidden = !foreign;
        this.number.required = foreign;
        this.number.disabled = !foreign;
        this.number.setAttribute('aria-required', String(foreign));
        this.type.value = next.type || '';
        this.country.setCustomValidity(next.error === 'country' ? this.dataset.countryMessage : '');
        this.number.setCustomValidity(next.error === 'number' ? this.dataset.numberMessage : '');
        const showError = next.status === 'error' || (this.showErrors && next.status !== 'saving' && Boolean(next.error));
        this.error.hidden = !showError;
        this.error.textContent = showError ? this.dataset[`${next.error}Message`] || this.dataset.saveMessage : '';
        this.retry.hidden = next.status !== 'error';
        this.country.setAttribute('aria-invalid', String(showError && next.error === 'country'));
        this.number.setAttribute('aria-invalid', String(showError && next.error === 'number'));
        this.toggleAttribute('aria-busy', next.status === 'saving' || next.status === 'loading');
        this.status.textContent = next.status === 'saving' ? this.dataset.savingMessage :
          (next.ready && foreign ? this.dataset.savedMessage : '');
      }
    });
  }
})();
